import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    IconButton,
    CircularProgress,
    Alert,
    Divider,
    TextField,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon, ListAlt as BOMIcon, Inventory2Outlined as PullIcon } from "@mui/icons-material";
import BOMService from "../../../services/BOM.service";
import WorkOrderService from "../../../services/WorkOrder.service";

function formatQty(val) {
    if (val === null || val === undefined || val === "") return "0";
    const n = parseFloat(val);
    if (Number.isNaN(n)) return String(val);
    return Math.ceil(n).toString();
}

// A line's quantities display in whichever UOM was last saved to
// BOMLine.display_uom (shared server-side — matches the desktop app),
// falling back to the part's primary UOM.
function getUomOptionsForLine(line) {
    const options = Array.isArray(line?.material_uom_options)
        ? line.material_uom_options
        : [];
    if (options.length > 0) return options;
    return [
        {
            id: null,
            name: line?.material_uom || "",
            abbreviation: line?.material_uom || "",
            factor_to_primary: "1",
        },
    ];
}

function getSelectedUomForLine(line) {
    const options = getUomOptionsForLine(line);
    return options.find((opt) => opt.id === line?.display_uom) || options[0];
}

function uomFactorToPrimary(uomOption) {
    const factor = Number(uomOption?.factor_to_primary);
    return Number.isFinite(factor) && factor > 0 ? factor : 1;
}

function convertFromPrimary(value, uomOption) {
    const qty = Number(value);
    if (!Number.isFinite(qty)) return value;
    return qty / uomFactorToPrimary(uomOption);
}

const STATUS_META = {
    pending: { label: "Pending", color: "default" },
    in_stock: { label: "In Stock", color: "success" },
    pulled: { label: "Pulled to Job", color: "info" },
    short: { label: "Short", color: "warning" },
    order_req: { label: "Order Required", color: "error" },
    on_order: { label: "On Order", color: "warning" },
    mo_req: { label: "WO Required", color: "secondary" },
    in_production: { label: "In Production", color: "info" },
    wo_complete: { label: "WO Complete", color: "success" },
    custom: { label: "Custom", color: "default" },
};

const WO_STATUS_COLOR = {
    draft: "default",
    released: "primary",
    in_progress: "warning",
    complete: "success",
    on_hold: "warning",
    cancelled: "error",
};

function isAssemblyLine(line) {
    return line.part_type === "AS";
}

function isCustomLine(line) {
    return line.part_type === "CUSTOM";
}

function getMatchingWOs(line, projectWOs) {
    if (!line.content_type) return [];
    return projectWOs.filter(
        (wo) =>
            wo.content_type === line.content_type &&
            Number(wo.object_id) === Number(line.object_id),
    );
}

// Assemblies aren't pulled from stock — they're built via a work order, so
// their status is derived from the linked WO's state rather than qty fields.
// An outsourced assembly (bought fully-built from a vendor) instead follows
// the same order/receive flow as a raw material.
function getEffectiveStatus(line, projectWOs) {
    if (isCustomLine(line)) return "custom";
    if (!isAssemblyLine(line) || line.is_outsourced) {
        // Trust any explicitly-set status except pending — "pending" just
        // means Check Stock hasn't run since the last change, so derive a
        // live status from on-hand qty instead of showing a stale label.
        if (line.status !== "pending") return line.status;
        const qtyOnHand = Number(line.qty_on_hand) || 0;
        const stillNeeded = Math.max(
            0,
            (Number(line.qty_required) || 0) - (Number(line.qty_from_stock) || 0),
        );
        if (stillNeeded <= 0) return "pulled";
        if (qtyOnHand >= stillNeeded) return "in_stock";
        if (qtyOnHand > 0) return "short";
        return "pending";
    }
    if (line.status === "pulled") return "pulled";

    const wos = getMatchingWOs(line, projectWOs);
    const activeWOs = wos.filter((wo) => wo.status !== "cancelled");

    if (activeWOs.length > 0) {
        if (activeWOs.find((wo) => wo.status === "complete")) return "wo_complete";
        if (activeWOs.find((wo) => ["released", "in_progress"].includes(wo.status)))
            return "in_production";
        return "pending";
    }

    if (["in_stock", "short"].includes(line.status)) return line.status;
    const qtyOnHand = Number(line.qty_on_hand) || 0;
    const stillNeeded = Math.max(
        0,
        (Number(line.qty_required) || 0) - (Number(line.qty_from_stock) || 0),
    );
    if (stillNeeded <= 0) return "pulled";
    if (qtyOnHand >= stillNeeded) return "in_stock";
    if (qtyOnHand > 0) return "short";
    return "mo_req";
}

export default function BOMPreviewDialog({ open, onClose, project }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [lines, setLines] = useState([]);
    const [projectWOs, setProjectWOs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedLineId, setSelectedLineId] = useState(null);
    const [pullOpenId, setPullOpenId] = useState(null);
    const [pullQty, setPullQty] = useState("");
    const [pullError, setPullError] = useState("");
    const [actioningLineId, setActioningLineId] = useState(null);

    useEffect(() => {
        if (!open || !project) return;
        setLoading(true);
        setError("");
        setSelectedLineId(null);
        setPullOpenId(null);
        setPullQty("");
        setPullError("");
        Promise.all([
            BOMService.listLines(project.project_id, project.bom_id),
            WorkOrderService.listWorkOrders({ project: project.project_id, page_size: 200 }),
            WorkOrderService.listWorkOrders({ project: project.project_id, archived: "1", page_size: 200 }),
        ])
            .then(([bomRes, woRes, woArchivedRes]) => {
                setLines(Array.isArray(bomRes.data) ? bomRes.data : bomRes.data.results || []);
                setProjectWOs([
                    ...(woRes.data.results || []),
                    ...(woArchivedRes.data.results || []),
                ]);
            })
            .catch(() => setError("Failed to load BOM lines."))
            .finally(() => setLoading(false));
    }, [open, project]);

    if (!project) return null;

    function openPull(line, uomOption) {
        const stillNeeded = Math.max(
            0,
            convertFromPrimary((Number(line.qty_required) || 0) - (Number(line.qty_from_stock) || 0), uomOption),
        );
        setPullOpenId(line.id);
        setPullQty(stillNeeded > 0 ? String(Math.round(stillNeeded * 100) / 100) : "");
        setPullError("");
    }

    function closePull() {
        setPullOpenId(null);
        setPullQty("");
        setPullError("");
    }

    function applyLineUpdate(updatedLine) {
        setLines((prev) => prev.map((l) => (l.id === updatedLine.id ? updatedLine : l)));
    }

    async function confirmPull(line, uomOption) {
        const qtyDisplay = parseFloat(pullQty);
        if (!Number.isFinite(qtyDisplay) || qtyDisplay <= 0) {
            setPullError("Enter a quantity greater than 0.");
            return;
        }
        const qtyPrimary = qtyDisplay * uomFactorToPrimary(uomOption);
        setActioningLineId(line.id);
        setPullError("");
        try {
            const res = await BOMService.pullStock(project.project_id, project.bom_id, line.id, qtyPrimary);
            applyLineUpdate(res.data.line);
            closePull();
        } catch (err) {
            setPullError(err?.response?.data?.detail || "Failed to pull stock.");
        } finally {
            setActioningLineId(null);
        }
    }

    async function undoPull(line) {
        setActioningLineId(line.id);
        try {
            const res = await BOMService.unpullStock(project.project_id, project.bom_id, line.id);
            applyLineUpdate(res.data.line);
        } catch (err) {
            setError(err?.response?.data?.detail || "Failed to undo pull.");
        } finally {
            setActioningLineId(null);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }} noWrap>
                            {project.project_number} · {project.project_name}
                            {project.bom_title ? ` — ${project.bom_title}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Rev {project.bom_revision}
                            {project.bom_status === "draft" ? " · Draft" : ""}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : lines.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                        <BOMIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                        <Typography variant="body2">No BOM lines found for this project.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {lines.map((line) => {
                            // An outsourced assembly is bought fully-built from a vendor, so it
                            // follows the raw-material order/pull UI rather than the WO-tracking UI.
                            const isAssembly = isAssemblyLine(line) && !line.is_outsourced;
                            const isCustom = isCustomLine(line);
                            const effectiveStatus = getEffectiveStatus(line, projectWOs);
                            const meta = STATUS_META[effectiveStatus] || {
                                label: effectiveStatus,
                                color: "default",
                            };
                            const matchedWOs = isAssembly ? getMatchingWOs(line, projectWOs) : [];
                            const uomOption = getSelectedUomForLine(line);
                            const uomAbbr = uomOption?.abbreviation || line.material_uom || "";
                            const isSelected = selectedLineId === line.id;
                            const isPulled = !isAssembly && effectiveStatus === "pulled";
                            const stillNeeded = Math.max(
                                0,
                                convertFromPrimary((Number(line.qty_required) || 0) - (Number(line.qty_from_stock) || 0), uomOption),
                            );
                            const canPull = stillNeeded > 0 && Number(line.qty_on_hand) > 0;
                            const cardAccentColor = isSelected
                                ? theme.palette.secondary.main
                                : isPulled
                                ? theme.palette.success.main
                                : null;
                            return (
                                <Box
                                    key={line.id}
                                    onClick={() => setSelectedLineId(isSelected ? null : line.id)}
                                    sx={{
                                        p: 1,
                                        border: 1,
                                        borderColor: cardAccentColor || "divider",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                                        boxShadow: cardAccentColor ? `0 0 0 1px ${cardAccentColor}` : "none",
                                    }}
                                >
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                {line.material_part_number || "—"}
                                                {line.part_type ? ` (${line.part_type})` : ""}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700} noWrap>
                                                {line.material_description || ""}
                                            </Typography>
                                        </Box>
                                        <Chip label={meta.label} size="small" color={meta.color} />
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
                                        {isCustom ? (
                                            <Chip
                                                label={`Req ${formatQty(convertFromPrimary(line.qty_required, uomOption))} ${uomAbbr}`.trim()}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ) : isAssembly ? (
                                            matchedWOs.length > 0 ? (
                                                matchedWOs.map((wo) => (
                                                    <Chip
                                                        key={wo.id}
                                                        label={wo.wo_number}
                                                        size="small"
                                                        variant="outlined"
                                                        color={WO_STATUS_COLOR[wo.status] || "default"}
                                                    />
                                                ))
                                            ) : (
                                                <Chip label="No work order" size="small" variant="outlined" />
                                            )
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    width: "100%",
                                                    border: 1,
                                                    borderColor: "divider",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {[
                                                    {
                                                        label: "Req",
                                                        value: `${formatQty(convertFromPrimary(line.qty_required, uomOption))} ${uomAbbr}`.trim(),
                                                        emphasis: true,
                                                    },
                                                    {
                                                        label: "From Stock",
                                                        value: formatQty(convertFromPrimary(line.qty_from_stock, uomOption)),
                                                    },
                                                    {
                                                        label: "To Order",
                                                        value: formatQty(convertFromPrimary(line.qty_to_order, uomOption)),
                                                    },
                                                    {
                                                        label: "On Hand",
                                                        value: formatQty(convertFromPrimary(line.qty_on_hand, uomOption)),
                                                    },
                                                ].map((stat, i, arr) => (
                                                    <Box
                                                        key={stat.label}
                                                        sx={{
                                                            flex: stat.emphasis ? 1.15 : 1,
                                                            minWidth: 0,
                                                            textAlign: "center",
                                                            py: stat.emphasis ? 0.75 : 0.5,
                                                            px: 0.5,
                                                            borderRight: i < arr.length - 1 ? 1 : 0,
                                                            borderColor: "divider",
                                                            bgcolor: stat.emphasis
                                                                ? isPulled
                                                                    ? "success.main"
                                                                    : "primary.main"
                                                                : "transparent",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            noWrap
                                                            sx={{
                                                                fontSize: "0.65rem",
                                                                textTransform: "uppercase",
                                                                letterSpacing: 0.3,
                                                                lineHeight: 1.4,
                                                                fontWeight: stat.emphasis ? 700 : 400,
                                                                color: stat.emphasis
                                                                    ? isPulled
                                                                        ? "success.contrastText"
                                                                        : "primary.contrastText"
                                                                    : "text.secondary",
                                                                opacity: stat.emphasis ? 0.85 : 1,
                                                            }}
                                                        >
                                                            {stat.label}
                                                        </Typography>
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontWeight: 800,
                                                                fontSize: "0.875rem",
                                                                lineHeight: 1.3,
                                                                color: stat.emphasis
                                                                    ? isPulled
                                                                        ? "success.contrastText"
                                                                        : "primary.contrastText"
                                                                    : "text.primary",
                                                            }}
                                                        >
                                                            {stat.value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                    {!isAssembly && !isCustom && (
                                        <Box sx={{ mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
                                            {pullOpenId === line.id ? (
                                                <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        autoFocus
                                                        label={`Qty (${uomAbbr})`}
                                                        value={pullQty}
                                                        onChange={(e) => setPullQty(e.target.value)}
                                                        inputProps={{ min: 0, step: "any", inputMode: "decimal" }}
                                                        disabled={actioningLineId === line.id}
                                                        sx={{ flex: 1 }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => confirmPull(line, uomOption)}
                                                        disabled={actioningLineId === line.id}
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        {actioningLineId === line.id ? (
                                                            <CircularProgress size={16} color="inherit" />
                                                        ) : (
                                                            "Pull"
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        onClick={closePull}
                                                        disabled={actioningLineId === line.id}
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                    {stillNeeded > 0 && (
                                                        <Tooltip
                                                            title={
                                                                canPull
                                                                    ? ""
                                                                    : "Nothing on hand to pull"
                                                            }
                                                        >
                                                            <span style={{ width: "100%" }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    disabled={!canPull}
                                                                    startIcon={<PullIcon fontSize="small" />}
                                                                    onClick={() => openPull(line, uomOption)}
                                                                >
                                                                    Pull Stock
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {Number(line.qty_from_stock) > 0 && (
                                                        <Button
                                                            size="small"
                                                            color="inherit"
                                                            onClick={() => undoPull(line)}
                                                            disabled={actioningLineId === line.id}
                                                        >
                                                            {actioningLineId === line.id ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                "Undo Pull"
                                                            )}
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                            {pullOpenId === line.id && pullError && (
                                                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                                    {pullError}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    {line.notes && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            {line.notes}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
